import type { Injector } from '@angular/core';
import { chain } from 'lodash';
import { DateTime } from 'luxon';
import { DelegationStatus } from '../../../dashboard/delegations/common/enums/delegation-status';
import { DAY_TIME_RANGE } from '../../constants/common/day-time-range';
import type {
  TelematicsDelegation,
  TelematicsGanttDelegation,
} from '../../interfaces/telematics/telematics-delegation';
import type { TelematicsFleetData } from '../../interfaces/telematics/telematics-event';
import { TelematicsStore } from '../../stores/telematics.store';

export class TelematicsDelegationFactory {
  public static from(data: {
    fleet: TelematicsFleetData;
    now: DateTime;
    injector: Injector;
  }): {
    activeDelegations: TelematicsDelegation[];
    delegations: TelematicsDelegation[];
    ganttDelegations: TelematicsGanttDelegation[];
  } {
    const telematicsStore = data.injector.get(TelematicsStore);

    const delegations: TelematicsDelegation[] = chain(data.fleet.delegations)
      .map((delegation) => {
        const status = delegation.status.value;
        const plannedEnd = delegation.end.date
          ? DateTime.fromISO(
              `${delegation.end.date}T${delegation.end.time ?? DAY_TIME_RANGE.to}`,
            )
          : null;

        return {
          status,
          start: DateTime.fromISO(
            `${delegation.start.date}T${delegation.start.time}`,
          ),
          plannedEnd,
          end:
            status === DelegationStatus.ACTIVE && plannedEnd
              ? DateTime.max(plannedEnd, data.now)
              : plannedEnd,
          delegation,
          id: delegation.uuid,
        };
      })
      .orderBy((delegation) => delegation.start)
      .value();
    const ganttDelegations: TelematicsGanttDelegation[] = [];

    let lastEnd: DateTime | null = telematicsStore.DATA_START();
    for (const delegation of delegations) {
      if (!lastEnd) break;

      if (delegation.start > lastEnd) {
        ganttDelegations.push({
          status: null,
          start: lastEnd,
          end: delegation.start,
          id: `empty-${delegation.delegation.uuid}`,
        });
      }

      ganttDelegations.push(delegation);

      lastEnd = delegation.end;
    }

    if (lastEnd != null) {
      ganttDelegations.push({
        status: null,
        start: lastEnd,
        end: null,
        id: 'empty',
      });
    }

    const activeDelegations = ganttDelegations.filter(
      (delegation): delegation is TelematicsDelegation =>
        delegation.status === DelegationStatus.ACTIVE,
    );

    return { activeDelegations, delegations, ganttDelegations };
  }
}
